/*!
 * Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: Apache-2.0
 */
import { ApolloLink, Observable, Operation, NextLink } from '@apollo/client';
import { Auth } from '@aws-amplify/auth';
import { print } from 'graphql/language/printer';
import { FetchResult } from '@apollo/client';
import { VariableDefinitionNode, visit } from 'graphql';
import { signSigV4Headers } from './awsSigV4';

export enum AUTH_TYPE {
  NONE = 'NONE',
  API_KEY = 'API_KEY',
  AWS_IAM = 'AWS_IAM',
  AMAZON_COGNITO_USER_POOLS = 'AMAZON_COGNITO_USER_POOLS',
  OPENID_CONNECT = 'OPENID_CONNECT',
}

export type APIGWLinkOptions = {
  uri: string;
  region: string;
  auth: AuthOptions;
};

type CredentialsOptions = {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
};

type Credentials = CredentialsOptions & {
  getPromise?: () => Promise<void>;
};

export default class AuthLink extends ApolloLink {
  private link: ApolloLink;

  /**
   *
   * @param {*} options
   */
  constructor(options: APIGWLinkOptions) {
    super();

    this.link = authLink(options);
  }

  request(operation: Operation, forward?: NextLink) {
    return this.link.request(operation, forward);
  }
}

interface Headers {
  header: string;
  value: string | (() => string | Promise<string>);
}

const headerBasedAuth = async (
  { header, value }: Headers = { header: '', value: '' },
  operation: Operation,
  forward: NextLink
) => {
  const origContext = operation.getContext();
  let headers = {
    ...origContext.headers,
  };

  if (header && value) {
    const headerValue =
      typeof value === 'function' ? await value.call(undefined) : await value;

    headers = {
      ...{ [header]: headerValue },
      ...headers,
    };
  }

  operation.setContext({
    ...origContext,
    headers,
  });

  return forward(operation);
};

const iamBasedAuth = async (
  {
    credentials,
    region,
    url,
  }: {
    credentials: AuthOptionsIAM['credentials'];
    region: string;
    url: string;
  },
  operation: Operation,
  forward: NextLink
) => {
  const origContext = operation.getContext();
  let creds:
    | undefined
    | CredentialsOptions
    | Promise<Credentials | CredentialsOptions | null> = undefined;

  try {
    creds =
      typeof credentials === 'function'
        ? credentials.call(undefined)
        : credentials || Auth.currentCredentials();

    const credentialObject = creds as Credentials;
    if (creds && typeof credentialObject.getPromise === 'function') {
      await credentialObject.getPromise();
    }
  } catch {
    return new Observable((subscriber) => {
      subscriber.error('error getting credentials');
    });
  }

  if (!creds) return forward(operation);
  let result: any = {};
  try {
    result = await (creds as Credentials);
  } catch {
    return new Observable((subscriber) => {
      subscriber.error('error getting credentials');
    });
  }
  let { accessKeyId, secretAccessKey, sessionToken } = result;

  let drift = await Auth.currentAuthenticatedUser({
    bypassCache: false, // this would get the latest clockDrift but takes ~1.5s
  })
    .then((user) => user.signInUserSession.clockDrift * 1000)
    .catch(() => 0);

  // The signing date carries the clock drift; the signer sets X-Amz-Date.
  const formatted = formatAsRequest(operation, {});
  const headers = await signSigV4Headers(
    {
      url,
      method: formatted.method,
      headers: formatted.headers,
      body: formatted.body,
    },
    { accessKeyId, secretAccessKey, sessionToken },
    region,
    new Date(Date.now() - drift)
  );

  operation.setContext({
    ...origContext,
    headers: {
      ...origContext.headers,
      ...headers,
    },
  });

  return forward(operation);
};

type KeysWithType<O, T> = {
  [K in keyof O]: O[K] extends T ? K : never;
}[keyof O];
type AuthOptionsNone = { type: AUTH_TYPE.NONE };
type AuthOptionsIAM = {
  type: KeysWithType<typeof AUTH_TYPE, AUTH_TYPE.AWS_IAM>;
  credentials:
    | (() =>
        | Credentials
        | CredentialsOptions
        | Promise<Credentials | CredentialsOptions | null>)
    | Credentials
    | CredentialsOptions
    | null;
};
type AuthOptionsApiKey = {
  type: KeysWithType<typeof AUTH_TYPE, AUTH_TYPE.API_KEY>;
  apiKey: (() => string | Promise<string>) | string;
};
type AuthOptionsOAuth = {
  type:
    | KeysWithType<typeof AUTH_TYPE, AUTH_TYPE.AMAZON_COGNITO_USER_POOLS>
    | KeysWithType<typeof AUTH_TYPE, AUTH_TYPE.OPENID_CONNECT>;
  jwtToken: (() => string | Promise<string>) | string;
};
export type AuthOptions =
  | AuthOptionsNone
  | AuthOptionsIAM
  | AuthOptionsApiKey
  | AuthOptionsOAuth;

export const authLink = ({
  uri: url,
  region,
  auth: { type } = {} as AuthOptions,
  auth,
}: APIGWLinkOptions) => {
  return new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      let handle: ReturnType<Observable<any>['subscribe']> | undefined;

      let promise: Promise<Observable<any>>;

      switch (type) {
        case AUTH_TYPE.NONE:
          promise = headerBasedAuth(undefined, operation, forward);
          break;
        case AUTH_TYPE.AWS_IAM:
          const { credentials } = auth as AuthOptionsIAM;
          promise = iamBasedAuth(
            {
              credentials,
              region,
              url,
            },
            operation,
            forward
          );
          break;
        case AUTH_TYPE.API_KEY:
          const { apiKey = '' } = auth as AuthOptionsApiKey;
          promise = headerBasedAuth(
            { header: 'X-Api-Key', value: apiKey },
            operation,
            forward
          );
          break;
        case AUTH_TYPE.AMAZON_COGNITO_USER_POOLS:
        case AUTH_TYPE.OPENID_CONNECT:
          const { jwtToken = '' } = auth as AuthOptionsOAuth;
          promise = headerBasedAuth(
            { header: 'Authorization', value: jwtToken },
            operation,
            forward
          );
          break;
        default:
          const error = new Error(`Invalid AUTH_TYPE: ${auth.type}`);

          throw error;
      }

      promise
        .then((observable: Observable<FetchResult>) => {
          handle = observable.subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch(() => {
          return new Observable((subscriber) => {
            subscriber.error('error getting credentials');
          });
        });

      return () => {
        if (handle) handle.unsubscribe();
      };
    });
  });
};

const formatAsRequest = (operation: Operation, options: any) => {
  const { operationName, variables, query } = operation;
  const body = {
    operationName,
    variables: removeUnusedVariables(
      operation,
      removeTemporaryVariables(variables)
    ),
    query: print(query),
  };

  return {
    body: JSON.stringify(body),
    method: 'POST',
    ...options,
    headers: {
      accept: '*/*',
      'content-type': 'application/json; charset=UTF-8',
      ...options.headers,
    },
  };
};

/**
 * Removes all temporary variables (starting with '@@') so that the signature matches the final request.
 */
const removeTemporaryVariables = (variables: any) =>
  Object.keys(variables)
    .filter((key) => !key.startsWith('@@'))
    .reduce(
      (acc, key) => {
        acc[key] = variables[key];
        return acc;
      },
      {} as Record<any, any>
    );

// Robado de @apollo/client/link/http/createHttpLink.ts
const removeUnusedVariables = (operation: Operation, variables: any) => {
  const unusedNames = new Set(Object.keys(variables));
  visit(operation.query, {
    Variable(node, _key, parent) {
      // A variable type definition at the top level of a query is not
      // enough to silence server-side errors about the variable being
      // unused, so variable definitions do not count as usage.
      // https://spec.graphql.org/draft/#sec-All-Variables-Used
      if (
        parent &&
        (parent as VariableDefinitionNode).kind !== 'VariableDefinition'
      ) {
        unusedNames.delete(node.name.value);
      }
    },
  });
  const cleanVariables = { ...variables };
  if (unusedNames.size) {
    // Make a shallow copy of body.variables (with keys in the same
    // order) and then delete unused variables from the copy.
    unusedNames.forEach((name) => {
      delete cleanVariables![name];
    });
  }
  return cleanVariables;
};
